/**
 * The five viewport widths the viewer can frame a prototype at (spec R3.3).
 *
 * These are *element* widths, not device emulation: the iframe is resized and
 * the page is centred, nothing else changes. That distinction matters because a
 * prototype's own media queries are the whole point of switching — an emulated
 * device would also change the user agent and pixel ratio, and a reviewer
 * looking at "Tablet" wants to know what the CSS does at 768, not what an iPad
 * would do.
 *
 * There is deliberately no fit-to-window preset. A window-relative width has no
 * stable pixel value, so a pin placed at "fit" recorded whatever the reviewer's
 * window happened to be (e.g. 2174) and then, on re-selection, snapped to the
 * nearest fixed preset — presenting a spurious "switch to Desktop". Every preset
 * now has a fixed width, so a recorded width always round-trips to itself.
 */

export type ViewerWidthPresetId = 'desktop' | 'laptop' | 'tablet' | 'mobile';

export interface ViewerWidthPreset {
  readonly id: ViewerWidthPresetId;
  /** Shown in the control. */
  readonly label: string;
  /** Rendered beside the label, e.g. `1440`. */
  readonly hint: string;
  /** Fixed frame width in CSS pixels. */
  readonly width: number;
}

/**
 * Ordered widest-first, which is also how a reviewer reads them: the default
 * case at the top, the exceptions below it.
 */
export const VIEWER_WIDTH_PRESETS: readonly ViewerWidthPreset[] = [
  { id: 'desktop', label: 'Desktop', hint: '1440', width: 1440 },
  { id: 'laptop', label: 'Laptop', hint: '1280', width: 1280 },
  { id: 'tablet', label: 'Tablet', hint: '768', width: 768 },
  { id: 'mobile', label: 'Mobile', hint: '390', width: 390 },
] as const;

/**
 * Desktop (1440), a fixed width, so every pin is recorded at a reproducible
 * viewport that re-selects to itself. On a narrower window the canvas simply
 * scrolls the centred frame — a predictable trade for stable pin geometry.
 */
export const DEFAULT_WIDTH_PRESET_ID: ViewerWidthPresetId = 'desktop';

const PRESETS_BY_ID: ReadonlyMap<ViewerWidthPresetId, ViewerWidthPreset> =
  new Map(VIEWER_WIDTH_PRESETS.map((preset) => [preset.id, preset]));

export const isViewerWidthPresetId = (
  value: unknown,
): value is ViewerWidthPresetId =>
  typeof value === 'string' &&
  PRESETS_BY_ID.has(value as ViewerWidthPresetId);

/** The preset for an id, falling back to the default for anything unknown. */
export const getWidthPreset = (id: ViewerWidthPresetId): ViewerWidthPreset =>
  PRESETS_BY_ID.get(id) ??
  (PRESETS_BY_ID.get(DEFAULT_WIDTH_PRESET_ID) as ViewerWidthPreset);

/** The frame's fixed width in pixels for a preset. */
export const resolveFrameWidth = (id: ViewerWidthPresetId): number =>
  getWidthPreset(id).width;

/**
 * The preset that best represents a recorded viewport width.
 *
 * Spec R3.6: selecting a thread switches the viewer to the width the pin was
 * created at. Threads record the exact pixel width of the frame at the time.
 * Now that every preset is a fixed width, a pin created at one round-trips to
 * itself; a width from an older fit-to-window session lands on the nearest
 * preset by absolute difference, ties going to the wider one, because a pin
 * placed in a layout's desktop breakpoint should not be re-shown at tablet.
 */
export const presetForViewportWidth = (
  viewportWidth: number,
): ViewerWidthPresetId => {
  const sized = VIEWER_WIDTH_PRESETS;

  if (!Number.isFinite(viewportWidth)) {
    return DEFAULT_WIDTH_PRESET_ID;
  }

  return sized.reduce((best, candidate) => {
    const bestDistance = Math.abs(best.width - viewportWidth);
    const candidateDistance = Math.abs(candidate.width - viewportWidth);

    if (candidateDistance < bestDistance) {
      return candidate;
    }

    // Equal distance: keep the wider one. `sized` is ordered widest-first, so
    // `best` already is the wider, and a strict `<` above never displaces it.
    return best;
  }).id;
};
